import { useMemo } from 'react'

import {
  AGENT_BRIDGE_APP_ACTION_TYPE,
  AgentHostActionHandlers,
  AgentHostState,
  useAgentAppContextCollector,
  useAgentHostActions
} from '@inno-tools/agent-bridge'
import { InnosErrorBoundary } from '@inno-tools/container-bridge'
import { useQueryClient } from '@tanstack/react-query'
import { usePinArtifact } from 'modules/desktop/modules/widgets/components/AiArtifacts'
import { useWindowManager } from 'modules/desktop/pages/MainPage/components/WindowManager/hooks/useWindowManager'
import { useOpenWindowMarketplace } from 'modules/desktop/pages/MainPage/hooks/useOpenWindowMarketplace'
import { useWindowStore } from 'modules/desktop/pages/MainPage/stores'
import { GET_ARTIFACTS_QUERY_KEY } from 'shared/api/forgeApi'
import {
  PIN_AI_WIDGET_APP_ID,
  WIDGET_MARKETPLACE_APP_ID
} from 'shared/constants/agentBridge'
import { useActiveApp } from 'shared/hooks/useActiveApp'
import { logger } from 'shared/services/logger'
import { getWindowIframe } from 'shared/utils/getWindowIframe'

const AgentBridgeManagerInner = (): null => {
  const { openAppWindowByAppId, closeAppWindowByAppId } = useWindowManager()
  const openMarketplace = useOpenWindowMarketplace()
  const pinArtifact = usePinArtifact()
  const queryClient = useQueryClient()
  const openWindows = useWindowStore(state => state.windows)
  const activeApp = useActiveApp()
  const appContexts = useAgentAppContextCollector()

  const handlers = useMemo<AgentHostActionHandlers>(
    () => ({
      openApp: ({ app_id, path }) => {
        if (!app_id) return
        // Widget Marketplace isn't a registered app, use its dedicated opener
        if (app_id === WIDGET_MARKETPLACE_APP_ID) {
          // cache is stale right after save_as_widget, invalidate so the new tile shows up
          queryClient.invalidateQueries({ queryKey: [GET_ARTIFACTS_QUERY_KEY] })
          openMarketplace()
          return
        }
        if (app_id === PIN_AI_WIDGET_APP_ID) {
          // tolerate a leading slash in path
          const publicId = path?.replace(/^\/+/, '').trim()
          if (publicId) {
            // same staleness as above, refetch so the pinned tile resolves its metadata
            queryClient.invalidateQueries({
              queryKey: [GET_ARTIFACTS_QUERY_KEY]
            })
            pinArtifact(publicId)
          }
          return
        }
        openAppWindowByAppId(app_id, path)
      },
      closeApp: ({ app_id }) => closeAppWindowByAppId(app_id),
      dispatchAppAction: ({ app_id, action, params, request_id }) => {
        if (!app_id || !action) return
        const iframe = getWindowIframe(app_id)
        const target = iframe?.contentWindow
        if (!target) {
          logger.warn('agent_bridge.target_unavailable', {
            appId: app_id,
            action,
            requestId: request_id
          })
          return
        }
        target.postMessage(
          {
            type: AGENT_BRIDGE_APP_ACTION_TYPE,
            appId: app_id,
            action,
            params,
            request_id
          },
          '*'
        )
      }
    }),
    [
      openAppWindowByAppId,
      closeAppWindowByAppId,
      openMarketplace,
      pinArtifact,
      queryClient
    ]
  )

  const state = useMemo<AgentHostState>(
    () => ({
      openApps: openWindows.map(w => ({
        id: w.id,
        path: 'url' in w && w.url ? w.url : '',
        name: w.title
      })),
      // covers both window-based and route-based active apps
      activeAppId: activeApp?.app.id,
      appContexts
    }),
    [openWindows, activeApp, appContexts]
  )

  useAgentHostActions(handlers, { state })

  return null
}

export const AgentBridgeManager = (): JSX.Element => (
  <InnosErrorBoundary debugName='AgentBridgeManager'>
    <AgentBridgeManagerInner />
  </InnosErrorBoundary>
)
